import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import { courseService } from '../../api/courseService';
import type { Lesson } from '../../api/courseService';
import { getVideoUrl } from '../../utils';

interface LessonFormProps {
  lesson?: Lesson;
  onSubmit: (data: { title: string; content: string; order: number; video_url?: string; thumbnail_url?: string }) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  initialOrder?: number;
}

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  font-size: 1rem;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 0.25rem;
  font-size: 1rem;
  min-height: 200px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primaryLight};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 0.75rem 1.5rem;
  background-color: ${({ theme, $primary }) => $primary ? theme.colors.primary : 'transparent'};
  color: ${({ theme, $primary }) => $primary ? 'white' : theme.colors.text};
  border: ${({ theme, $primary }) => $primary ? 'none' : `1px solid ${theme.colors.border}`};
  border-radius: 0.25rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme, $primary }) => $primary ? theme.colors.primaryDark : theme.colors.background};
  }
  
  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray};
    cursor: not-allowed;
  }
`;

const FileInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
  position: relative;
`;

const DropZone = styled.div<{ $isDragging: boolean; $hasError: boolean }>`
  border: 2px dashed ${({ theme, $isDragging, $hasError }) => 
    $hasError ? theme.colors.danger : 
    $isDragging ? theme.colors.primary : theme.colors.border};
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  background-color: ${({ theme, $isDragging }) => 
    $isDragging ? `${theme.colors.primary}10` : theme.colors.background};
  transition: all 0.2s ease;
  cursor: pointer;
  margin-bottom: 1rem;

  &:hover {
    border-color: ${({ theme, $hasError }) => 
      $hasError ? theme.colors.danger : theme.colors.primary};
    background-color: ${({ theme }) => `${theme.colors.primary}10`};
  }
`;

const DropZoneText = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const DropZoneSubtext = styled.div`
  color: ${({ theme }) => theme.colors.gray};
  font-size: 0.875rem;
`;

const ProgressContainer = styled.div`
  margin-top: 1rem;
  width: 100%;
`;

const ProgressBar = styled.div<{ $progress: number }>`
  height: 8px;
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0.5rem;

  div {
    height: 100%;
    background-color: ${({ theme }) => theme.colors.primary};
    width: ${props => `${props.$progress}%`};
    transition: width 0.3s ease;
  }
`;

const ProgressText = styled.div`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.gray};
  text-align: center;
`;

const ErrorMessage = styled.div`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 0.875rem;
  margin-top: 0.5rem;
  text-align: center;
`;

const VideoPreview = styled.div`
  margin-top: 1rem;
  position: relative;
  
  video {
    max-width: 100%;
    max-height: 300px;
    border-radius: 0.5rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  background-color: ${({ theme }) => theme.colors.danger};
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const CurrentVideo = styled.div`
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: ${({ theme }) => theme.colors.light};
  border-radius: 0.25rem;
  font-size: 0.9rem;
  
  a {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  padding: 0.5rem;
  background-color: #e6ffe6;
  border-radius: 4px;
`;

const SuccessIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #4caf50;
  color: white;
  border-radius: 50%;
  margin-right: 0.5rem;
  font-weight: bold;
`;

const SuccessText = styled.span`
  color: #2e7d32;
  font-weight: 500;
`;

const LessonForm: React.FC<LessonFormProps> = ({
  lesson,
  onSubmit,
  onCancel,
  isSubmitting = false,
  initialOrder = 1,
}) => {
  const [title, setTitle] = useState(lesson?.title || '');
  const [content, setContent] = useState(lesson?.content || '');
  const [order, setOrder] = useState(
    lesson?.order_index ?? lesson?.order ?? initialOrder
  );
  const [videoUrl, setVideoUrl] = useState(lesson?.video_url || '');
  const [thumbnailUrl, setThumbnailUrl] = useState(lesson?.thumbnail_url || '');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Video upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ lessonId, file }: { lessonId: string; file: File }) => 
      courseService.uploadLessonVideo(
        lessonId, 
        file,
        (progress) => {
          setUploadProgress(progress);
        }
      ),
    onSuccess: (response: any) => {
      const updatedLesson = response.data || {};
      setVideoUrl(updatedLesson.video_url || '');
      setIsUploading(false);
      setUploadProgress(100);
      setUploadError(null);
      setUploadSuccess(true);
    },
    onError: (error: any) => {
      console.error('Error uploading video:', error);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadSuccess(false);
      
      // Display specific error message
      if (error.code === 'ERR_FILE_TOO_LARGE') {
        setUploadError(error.message || 'File size exceeds the maximum allowed limit.');
      } else if (error.code === 'ERR_UNSUPPORTED_FORMAT') {
        setUploadError(error.message || 'Unsupported video format. Please use MP4, WebM, MOV, AVI, WMV, FLV, or MKV.');
      } else if (error.message && error.message.includes('413')) {
        setUploadError('File size is too large. Please upload a smaller video file.');
      } else if (error.code === 'ERR_NETWORK') {
        setUploadError('Network error during upload. This may be due to CORS restrictions. Please try a smaller file or contact support.');
      } else {
        setUploadError(`Failed to upload video: ${error.message || 'Unknown error'}. Try a smaller file or a different format.`);
      }
    },
  });

  useEffect(() => {
    if (lesson) {
      setTitle(lesson.title || '');
      setContent(lesson.content || '');
      setOrder(lesson.order_index ?? lesson.order ?? initialOrder);
      setVideoUrl(lesson.video_url || '');
      setThumbnailUrl(lesson.thumbnail_url || '');
    }
  }, [lesson, initialOrder]);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter === 1) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleVideoChange({ target: { files: [file] } } as any);
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      // Validate file size
      const MAX_FILE_SIZE = 1024 * 1024 * 1024; // 1GB
      if (file.size > MAX_FILE_SIZE) {
        setUploadError(`File size exceeds limit. Maximum allowed size is ${MAX_FILE_SIZE / (1024 * 1024)}MB.`);
        return;
      }

      // Validate file format
      const supportedFormats = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 
                               'video/x-ms-wmv', 'video/x-flv', 'video/x-matroska'];
      const fileType = file.type;
      
      if (!supportedFormats.includes(fileType) && 
          !supportedFormats.some(format => file.name.toLowerCase().endsWith(format.split('/')[1]))) {
        setUploadError('Unsupported video format. Supported formats: MP4, WebM, MOV, AVI, WMV, FLV, MKV');
        return;
      }

      setVideoFile(file);
      setUploadError(null);
      
      // Create a preview URL for the video
      const previewUrl = URL.createObjectURL(file);
      setVideoPreview(previewUrl);
    }
  };

  const handleRemoveVideo = () => {
    setVideoFile(null);
    setVideoPreview(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Upload video to server (only if we have a lesson ID)
  const handleUploadVideo = async () => {
    if (videoFile && lesson?.id) {
      setIsUploading(true);
      setUploadProgress(0);
      setUploadError(null);
      
      // Let user know about file size considerations
      if (videoFile.size > 50 * 1024 * 1024) { // 50MB
        alert('You are uploading a large file (over 50MB). The upload may take some time and timeout. If you encounter issues, try a smaller file or compress the video first.');
      }
      
      uploadMutation.mutate({ lessonId: lesson.id, file: videoFile });
    } else if (!lesson?.id) {
      alert('Please save the lesson first before uploading a video.');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title,
      content,
      order: Number(order),
      video_url: videoUrl || undefined,
      thumbnail_url: thumbnailUrl || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <FormGroup>
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="order">Order</Label>
        <Input
          id="order"
          type="number"
          min="1"
          value={order}
          onChange={(e) => setOrder(Number(e.target.value))}
          required
        />
      </FormGroup>
      
      <FormGroup>
        <Label htmlFor="content">Content</Label>
        <Textarea
          id="content"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          required
        />
      </FormGroup>

      <FormGroup>
        <Label htmlFor="thumbnailUrl">Thumbnail URL (optional)</Label>
        <Input
          id="thumbnailUrl"
          type="url"
          value={thumbnailUrl}
          onChange={(e) => setThumbnailUrl(e.target.value)}
          placeholder="https://example.com/thumbnail.jpg"
        />
      </FormGroup>
      
      <FormGroup>
        <Label>Video</Label>
        
        {/* Display current video URL if it exists */}
        {videoUrl && !videoFile && (
          <CurrentVideo>
            Current video: <a href={getVideoUrl(videoUrl)} target="_blank" rel="noopener noreferrer">{videoUrl}</a>
          </CurrentVideo>
        )}
        
        {/* Video upload option only available for existing lessons */}
        {lesson?.id ? (
          <FileInputContainer>
            <DropZone
              $isDragging={isDragging}
              $hasError={!!uploadError}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <DropZoneText>
                {videoFile ? 'Click to change video' : 'Drag & drop video here'}
              </DropZoneText>
              <DropZoneSubtext>
                or click to browse files
              </DropZoneSubtext>
              <DropZoneSubtext>
                Max size: 1GB • Supported formats: MP4, WebM, MOV, AVI, WMV, FLV, MKV
              </DropZoneSubtext>
            </DropZone>
            
            <input
              type="file"
              accept="video/*"
              onChange={handleVideoChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
            />
            
            {uploadError && (
              <ErrorMessage>
                {uploadError}
              </ErrorMessage>
            )}
            
            {videoPreview && (
              <VideoPreview>
                <video controls>
                  <source src={videoPreview} />
                  Your browser does not support the video tag.
                </video>
                <RemoveButton onClick={handleRemoveVideo} title="Remove video">
                  ×
                </RemoveButton>
              </VideoPreview>
            )}
            
            {videoFile && !isUploading && !uploadSuccess && (
              <Button 
                type="button" 
                $primary 
                onClick={handleUploadVideo} 
                style={{ marginTop: '1rem', alignSelf: 'flex-start' }}
              >
                Upload Video
              </Button>
            )}
            
            {isUploading && (
              <ProgressContainer>
                <ProgressBar $progress={uploadProgress}>
                  <div />
                </ProgressBar>
                <ProgressText>
                  Uploading... {uploadProgress}%
                </ProgressText>
              </ProgressContainer>
            )}
            
            {uploadSuccess && (
              <SuccessContainer>
                <SuccessIcon>✓</SuccessIcon>
                <SuccessText>Video uploaded successfully!</SuccessText>
              </SuccessContainer>
            )}
          </FileInputContainer>
        ) : (
          <p>You can upload a video after creating the lesson.</p>
        )}
      </FormGroup>
      
      <ButtonGroup>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" $primary disabled={isSubmitting || isUploading}>
          {isSubmitting ? 'Saving...' : lesson ? 'Update Lesson' : 'Create Lesson'}
        </Button>
      </ButtonGroup>
    </form>
  );
};

export default LessonForm; 